<template>
    <div class="doc-toolbar" :class="`doc-toolbar--level-${level}`" role="toolbar" aria-label="Formatação do texto">
        <!-- Historico e pincel -->
        <div class="doc-toolbar__group">
            <toolbar-button icon="mdi-undo" label="Desfazer (Ctrl+Z)" :disabled="!can('undo')" @click="run((c) => c.undo())" />
            <toolbar-button icon="mdi-redo" label="Refazer (Ctrl+Shift+Z)" :disabled="!can('redo')" @click="run((c) => c.redo())" />
            <toolbar-button icon="mdi-format-paint" label="Pincel de formatação: copia a formatação do trecho atual e aplica no próximo texto selecionado"
                :active="painterArmed" @click="$emit('toggle-painter')" />
        </div>

        <!-- Estilo, fonte e tamanho -->
        <div class="doc-toolbar__group">
            <v-menu offset-y :close-on-content-click="true">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__select doc-toolbar__select--style" v-bind="attrs" v-on="on" title="Estilo do parágrafo">
                        <span>{{ currentStyleLabel }}</span><v-icon small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu doc-toolbar__styles">
                    <v-subheader class="doc-toolbar__subheader">Parágrafo</v-subheader>
                    <v-list-item v-for="style in paragraphStyles" :key="style.key" :class="{ 'v-list-item--active': style.key === currentStyleKey }"
                        @click="run((c) => c.applyParagraphStyle(style))">
                        <v-list-item-title class="doc-style-preview" :style="stylePreview(style)">{{ style.name }}</v-list-item-title>
                    </v-list-item>
                    <template v-if="characterStyles.length">
                        <v-subheader class="doc-toolbar__subheader">Trecho</v-subheader>
                        <v-list-item v-for="style in characterStyles" :key="style.key" :class="{ 'v-list-item--active': style.key === currentCharacterKey }"
                            @click="run((c) => c.applyCharacterStyle(style))">
                            <v-list-item-title class="doc-style-preview" :style="stylePreview(style)">{{ style.name }}</v-list-item-title>
                        </v-list-item>
                    </template>
                    <v-divider class="my-1"></v-divider>
                    <v-list-item @click="$emit('toggle-styles')">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-format-list-text</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ stylesOpen ? "Fechar painel de estilos" : "Painel de estilos" }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__select doc-toolbar__select--font" v-bind="attrs" v-on="on" title="Fonte">
                        <span>{{ currentFontLabel }}</span><v-icon small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item v-for="font in fonts" :key="font.text" @click="setFont(font.value)">
                        <v-list-item-title :style="{ fontFamily: font.value || undefined }">{{ font.text }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <toolbar-button v-if="level < 1" icon="mdi-minus" label="Diminuir fonte" @click="stepFontSize(-1)" />
            <v-menu offset-y max-height="320">
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__select doc-toolbar__select--size" v-bind="attrs" v-on="on" title="Tamanho da fonte">
                        <span>{{ currentFontSize.replace("pt", "") }}</span><v-icon small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item v-for="size in fontSizes" :key="size" @click="run((c) => c.setFontSize(size))">
                        <v-list-item-title>{{ size.replace("pt", "") }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <toolbar-button v-if="level < 1" icon="mdi-plus" label="Aumentar fonte" @click="stepFontSize(1)" />
        </div>

        <!-- Marcas -->
        <div class="doc-toolbar__group">
            <toolbar-button icon="mdi-format-bold" label="Negrito (Ctrl+B)" :active="isActive('bold')" @click="run((c) => c.toggleBold())" />
            <toolbar-button icon="mdi-format-italic" label="Itálico (Ctrl+I)" :active="isActive('italic')" @click="run((c) => c.toggleItalic())" />
            <toolbar-button icon="mdi-format-underline" label="Sublinhado (Ctrl+U)" :active="isActive('underline')" @click="run((c) => c.toggleUnderline())" />
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn" :class="{ 'doc-toolbar__btn--active': extraMarkActive }" v-bind="attrs" v-on="on"
                        title="Tachado, subscrito e sobrescrito" @mousedown.prevent>
                        <v-icon size="18">mdi-format-text-variant-outline</v-icon><v-icon x-small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item v-for="mark in extraMarks" :key="mark.name" :class="{ 'v-list-item--active': isActive(mark.name) }" @click="run(mark.toggle)">
                        <v-list-item-icon class="mr-3"><v-icon size="18">{{ mark.icon }}</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ mark.label }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>

            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn doc-toolbar__btn--color" v-bind="attrs" v-on="on" title="Cor do texto (paleta da marca)">
                        <v-icon size="18">mdi-format-color-text</v-icon>
                        <span class="doc-toolbar__swatch" :style="{ background: currentColor || '#111111' }"></span>
                    </button>
                </template>
                <div class="doc-toolbar__palette">
                    <div class="doc-toolbar__palette-title">Cor do texto</div>
                    <button type="button" class="doc-toolbar__palette-item" @click="run((c) => c.unsetColor())">
                        <span class="doc-toolbar__palette-dot" style="background: #111111"></span>Automática
                    </button>
                    <button v-for="color in brandColors" :key="color.bg" type="button" class="doc-toolbar__palette-item" @click="run((c) => c.setColor(color.bg))">
                        <span class="doc-toolbar__palette-dot" :style="{ background: color.bg }"></span>{{ color.name }}
                    </button>
                </div>
            </v-menu>
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn doc-toolbar__btn--color" v-bind="attrs" v-on="on" title="Realce (paleta da marca)">
                        <v-icon size="18">mdi-marker</v-icon>
                        <span class="doc-toolbar__swatch" :style="{ background: currentHighlight || 'transparent' }"></span>
                    </button>
                </template>
                <div class="doc-toolbar__palette">
                    <div class="doc-toolbar__palette-title">Realce</div>
                    <button type="button" class="doc-toolbar__palette-item" @click="run((c) => c.unsetHighlight())">
                        <span class="doc-toolbar__palette-dot doc-toolbar__palette-dot--none"></span>Sem realce
                    </button>
                    <button v-for="color in brandColors" :key="color.bg" type="button" class="doc-toolbar__palette-item" @click="run((c) => c.setHighlight({ color: color.bg }))">
                        <span class="doc-toolbar__palette-dot" :style="{ background: color.bg }"></span>{{ color.name }}
                    </button>
                </div>
            </v-menu>
        </div>

        <!-- Paragrafo -->
        <div class="doc-toolbar__group">
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn" v-bind="attrs" v-on="on" title="Alinhamento" @mousedown.prevent>
                        <v-icon size="18">{{ currentAlign.icon }}</v-icon><v-icon x-small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item v-for="align in aligns" :key="align.value" :class="{ 'v-list-item--active': isAlign(align.value) }" @click="setAlign(align.value)">
                        <v-list-item-icon class="mr-3"><v-icon size="18">{{ align.icon }}</v-icon></v-list-item-icon>
                        <v-list-item-content><v-list-item-title>{{ align.label }}</v-list-item-title></v-list-item-content>
                        <v-list-item-action-text class="ml-4">{{ align.shortcut }}</v-list-item-action-text>
                    </v-list-item>
                </v-list>
            </v-menu>
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn" v-bind="attrs" v-on="on" title="Entrelinha">
                        <v-icon size="18">mdi-format-line-spacing</v-icon><v-icon x-small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item v-for="line in lineHeights" :key="line.text" @click="run((c) => c.setLineHeight(line.value))">
                        <v-list-item-title>{{ line.text }}</v-list-item-title>
                    </v-list-item>
                </v-list>
            </v-menu>
            <toolbar-button v-if="level < 2" icon="mdi-format-list-bulleted" label="Lista com marcadores" :active="isActive('bulletList')" @click="run((c) => c.toggleBulletList())" />
            <toolbar-button v-if="level < 2" icon="mdi-format-list-numbered" label="Lista numerada" :active="isActive('orderedList')" @click="run((c) => c.toggleOrderedList())" />
            <toolbar-button v-if="level < 2" icon="mdi-format-indent-decrease" label="Diminuir recuo (Shift+Tab)" @click="outdent" />
            <toolbar-button v-if="level < 2" icon="mdi-format-indent-increase" label="Aumentar recuo (Tab)" @click="indent" />
        </div>

        <!-- Inserir -->
        <div class="doc-toolbar__group">
            <toolbar-button v-if="level < 2" icon="mdi-format-quote-close" label="Citação (padrão da casa)" :active="isActive('blockquote')" @click="run((c) => c.toggleBlockquote())" />
            <toolbar-button v-if="level < 2" icon="mdi-link-variant" label="Link" :active="isActive('link')" @click="$emit('link')" />
            <v-menu offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn doc-toolbar__btn--text" v-bind="attrs" v-on="on" title="Inserir">
                        <v-icon size="18">mdi-plus-box-outline</v-icon><span v-if="level < 1">Inserir</span><v-icon x-small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item @click="run((c) => c.insertTable({ rows: 3, cols: 3, withHeaderRow: true }))">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-table</v-icon></v-list-item-icon><v-list-item-title>Tabela</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="$emit('image')">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-image-outline</v-icon></v-list-item-icon><v-list-item-title>Imagem</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="$emit('attachment')">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-paperclip</v-icon></v-list-item-icon><v-list-item-title>Anexo</v-list-item-title>
                    </v-list-item>
                    <v-list-item :class="{ 'v-list-item--active': isActive('footnote') }" @click="$emit('footnote')">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-note-text-outline</v-icon></v-list-item-icon>
                        <v-list-item-title>{{ isActive("footnote") ? "Editar nota de rodapé" : "Nota de rodapé" }}</v-list-item-title>
                    </v-list-item>
                    <v-divider class="my-1"></v-divider>
                    <v-list-item @click="run((c) => c.setHorizontalRule())">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-minus</v-icon></v-list-item-icon><v-list-item-title>Linha horizontal</v-list-item-title>
                    </v-list-item>
                    <v-list-item @click="run((c) => c.setPageBreak())">
                        <v-list-item-icon class="mr-3"><v-icon size="18">mdi-format-page-break</v-icon></v-list-item-icon>
                        <v-list-item-content><v-list-item-title>Quebra de página</v-list-item-title><v-list-item-subtitle>Ctrl+Enter</v-list-item-subtitle></v-list-item-content>
                    </v-list-item>
                    <template v-if="$slots.insert">
                        <v-divider class="my-1"></v-divider>
                        <slot name="insert"></slot>
                    </template>
                </v-list>
            </v-menu>
            <v-menu v-if="isActive('table')" offset-y>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn doc-toolbar__btn--text doc-toolbar__btn--context" v-bind="attrs" v-on="on" title="Tabela">
                        <v-icon size="18">mdi-table-edit</v-icon><span>Tabela</span><v-icon x-small>mdi-chevron-down</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <v-list-item @click="run((c) => c.addRowBefore())"><v-list-item-title>Linha acima</v-list-item-title></v-list-item>
                    <v-list-item @click="run((c) => c.addRowAfter())"><v-list-item-title>Linha abaixo</v-list-item-title></v-list-item>
                    <v-list-item @click="run((c) => c.addColumnBefore())"><v-list-item-title>Coluna à esquerda</v-list-item-title></v-list-item>
                    <v-list-item @click="run((c) => c.addColumnAfter())"><v-list-item-title>Coluna à direita</v-list-item-title></v-list-item>
                    <v-divider class="my-1"></v-divider>
                    <v-list-item @click="run((c) => c.toggleHeaderRow())"><v-list-item-title>Linha de cabeçalho</v-list-item-title></v-list-item>
                    <v-list-item @click="run((c) => c.mergeOrSplit())"><v-list-item-title>Mesclar ou separar células</v-list-item-title></v-list-item>
                    <v-subheader class="doc-toolbar__subheader">Cor da célula</v-subheader>
                    <div class="doc-toolbar__cell-colors">
                        <button v-for="color in brandColors" :key="color.bg" type="button" :title="color.name" class="doc-toolbar__cell-color" :style="{ background: color.bg }"
                            @click="setCellColor(color)"></button>
                        <button type="button" title="Sem cor" class="doc-toolbar__cell-color doc-toolbar__palette-dot--none" @click="setCellColor(null)"></button>
                    </div>
                    <v-divider class="my-1"></v-divider>
                    <v-list-item @click="run((c) => c.deleteRow())"><v-list-item-title class="doc-toolbar__danger">Excluir linha</v-list-item-title></v-list-item>
                    <v-list-item @click="run((c) => c.deleteColumn())"><v-list-item-title class="doc-toolbar__danger">Excluir coluna</v-list-item-title></v-list-item>
                    <v-list-item @click="run((c) => c.deleteTable())"><v-list-item-title class="doc-toolbar__danger">Excluir tabela</v-list-item-title></v-list-item>
                </v-list>
            </v-menu>
            <toolbar-button v-if="level < 2" icon="mdi-format-clear" label="Limpar formatação" @click="clearFormatting" />
            <v-menu v-if="level >= 2" offset-y left>
                <template v-slot:activator="{ on, attrs }">
                    <button type="button" class="doc-toolbar__btn" v-bind="attrs" v-on="on" title="Mais opções" aria-label="Mais opções" @mousedown.prevent>
                        <v-icon size="18">mdi-dots-horizontal</v-icon>
                    </button>
                </template>
                <v-list dense class="doc-toolbar__menu">
                    <template v-for="(item, index) in overflowItems">
                        <v-divider v-if="item.divider" :key="`d-${index}`" class="my-1"></v-divider>
                        <v-list-item v-else :key="item.label" :class="{ 'v-list-item--active': item.active }" @click="item.action()">
                            <v-list-item-icon class="mr-3"><v-icon size="18">{{ item.icon }}</v-icon></v-list-item-icon>
                            <v-list-item-content><v-list-item-title>{{ item.label }}</v-list-item-title></v-list-item-content>
                            <v-list-item-action-text v-if="item.shortcut" class="ml-4">{{ item.shortcut }}</v-list-item-action-text>
                        </v-list-item>
                    </template>
                </v-list>
            </v-menu>
            <slot name="extra"></slot>
        </div>


        <!-- Visualizacao -->
        <div class="doc-toolbar__group doc-toolbar__group--view">
            <toolbar-button icon="mdi-format-list-text" label="Painel de estilos" :active="stylesOpen" @click="$emit('toggle-styles')" />
            <toolbar-button icon="mdi-magnify" label="Localizar e substituir (Ctrl+F)" :active="searchOpen" @click="$emit('toggle-search')" />
            <template v-if="pageMode">
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="doc-toolbar__select doc-toolbar__select--zoom" v-bind="attrs" v-on="on" title="Zoom">
                            <span>{{ Math.round(zoom * 100) }}%</span><v-icon small>mdi-chevron-down</v-icon>
                        </button>
                    </template>
                    <v-list dense class="doc-toolbar__menu">
                        <v-list-item v-for="value in zooms" :key="value" @click="$emit('zoom', value)"><v-list-item-title>{{ value * 100 }}%</v-list-item-title></v-list-item>
                    </v-list>
                </v-menu>
                <v-menu offset-y>
                    <template v-slot:activator="{ on, attrs }">
                        <button type="button" class="doc-toolbar__btn" v-bind="attrs" v-on="on" title="Margens da folha">
                            <v-icon size="18">mdi-page-layout-body</v-icon><v-icon x-small>mdi-chevron-down</v-icon>
                        </button>
                    </template>
                    <v-list dense class="doc-toolbar__menu">
                        <v-subheader class="doc-toolbar__subheader">Orientação</v-subheader>
                        <v-list-item v-for="option in orientations" :key="option.value" :class="{ 'v-list-item--active': option.value === orientation }" @click="$emit('orientation', option.value)">
                            <v-list-item-icon class="mr-3"><v-icon size="18">{{ option.icon }}</v-icon></v-list-item-icon>
                            <v-list-item-title>{{ option.text }}</v-list-item-title>
                        </v-list-item>
                        <v-subheader class="doc-toolbar__subheader">Margens</v-subheader>
                        <v-list-item v-for="preset in marginPresets" :key="preset.key" :class="{ 'v-list-item--active': preset.key === marginKey }" @click="$emit('margins', preset.key)">
                            <v-list-item-content>
                                <v-list-item-title>{{ preset.text }}</v-list-item-title>
                                <v-list-item-subtitle>{{ formatMargins(preset) }}</v-list-item-subtitle>
                            </v-list-item-content>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </template>
            <toolbar-button :icon="pageMode ? 'mdi-text-box-outline' : 'mdi-file-document-outline'" :label="pageMode ? 'Ver como texto corrido' : 'Ver como folha (A4)'"
                :active="pageMode" @click="$emit('toggle-page')" />
            <toolbar-button v-if="canFullscreen" :icon="fullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen'" :label="fullscreen ? 'Sair da tela cheia (Esc)' : 'Tela cheia'" @click="$emit('toggle-fullscreen')" />
        </div>
    </div>
</template>

<script>
import { VIcon } from "vuetify/lib";
import { BRAND_COLORS, FONT_FAMILIES, FONT_SIZES, DEFAULT_FONT_SIZE, LINE_HEIGHTS, MARGIN_PRESETS } from "./brand";
import { activeStyles, findStyle, previewCss } from "./styles/style-store";
import { currentParagraphStyleKey, currentCharacterStyleKey } from "./extensions/styles";

const ToolbarButton = {
    name: "ToolbarButton",
    props: { icon: String, label: String, active: Boolean, disabled: Boolean },
    render(h) {
        return h("button", {
            class: ["doc-toolbar__btn", { "doc-toolbar__btn--active": this.active }],
            attrs: { type: "button", title: this.label, "aria-label": this.label, "aria-pressed": String(!!this.active), disabled: this.disabled },
            on: { mousedown: (event) => event.preventDefault(), click: (event) => this.$emit("click", event) },
        }, [h(VIcon, { props: { size: 18 } }, this.icon)]);
    },
};

// Barra de ferramentas do ui-document-editor. Recebe a instancia do editor
// e um contador (tick) que muda a cada transacao para atualizar os estados.
export default {
    name: "EditorToolbar",
    components: { ToolbarButton },
    props: {
        editor: { type: Object, required: true },
        tick: { type: Number, default: 0 },
        pageMode: { type: Boolean, default: false },
        fullscreen: { type: Boolean, default: false },
        canFullscreen: { type: Boolean, default: true },
        searchOpen: { type: Boolean, default: false },
        painterArmed: { type: Boolean, default: false },
        zoom: { type: Number, default: 1 },
        marginKey: { type: String, default: "bej" },
        orientation: { type: String, default: "portrait" },
        stylesOpen: { type: Boolean, default: false },
    },
    data: () => ({
        width: 0,
        brandColors: BRAND_COLORS,
        fonts: FONT_FAMILIES,
        fontSizes: FONT_SIZES,
        lineHeights: LINE_HEIGHTS,
        marginPresets: MARGIN_PRESETS,
        orientations: [
            { value: "portrait", text: "Retrato (21 × 29,7 cm)", icon: "mdi-crop-portrait" },
            { value: "landscape", text: "Paisagem (29,7 × 21 cm)", icon: "mdi-crop-landscape" },
        ],
        zooms: [0.75, 1, 1.25, 1.5],
        aligns: [
            { value: "left", icon: "mdi-format-align-left", label: "Alinhar à esquerda", shortcut: "Ctrl+Shift+L" },
            { value: "center", icon: "mdi-format-align-center", label: "Centralizar", shortcut: "Ctrl+Shift+E" },
            { value: "right", icon: "mdi-format-align-right", label: "Alinhar à direita", shortcut: "Ctrl+Shift+R" },
            { value: "justify", icon: "mdi-format-align-justify", label: "Justificar", shortcut: "Ctrl+Shift+J" },
        ],
        extraMarks: [
            { name: "strike", icon: "mdi-format-strikethrough", label: "Tachado", toggle: (c) => c.toggleStrike() },
            { name: "subscript", icon: "mdi-format-subscript", label: "Subscrito", toggle: (c) => c.toggleSubscript() },
            { name: "superscript", icon: "mdi-format-superscript", label: "Sobrescrito", toggle: (c) => c.toggleSuperscript() },
        ],
    }),
    computed: {
        paragraphStyles() {
            return activeStyles("paragraph");
        },
        characterStyles() {
            return activeStyles("character");
        },
        currentStyleKey() {
            void this.tick;
            return currentParagraphStyleKey(this.editor);
        },
        currentCharacterKey() {
            void this.tick;
            return currentCharacterStyleKey(this.editor);
        },
        currentStyleLabel() {
            const style = findStyle(this.currentStyleKey);
            return style ? style.name : "Texto normal";
        },
        // Itens que vao para o menu "Mais" quando a barra fica estreita
        // (nivel 1: some o -/+ da fonte e o texto "Inserir"; nivel 2: listas,
        // recuo, citacao, link e limpar formatacao vao para o menu).
        level() {
            if (!this.width || this.width >= 1240) return 0;
            return this.width >= 1120 ? 1 : 2;
        },
        overflowItems() {
            void this.tick;
            return [
                { icon: "mdi-format-list-bulleted", label: "Lista com marcadores", active: this.editor.isActive("bulletList"), action: () => this.run((c) => c.toggleBulletList()) },
                { icon: "mdi-format-list-numbered", label: "Lista numerada", active: this.editor.isActive("orderedList"), action: () => this.run((c) => c.toggleOrderedList()) },
                { icon: "mdi-format-indent-decrease", label: "Diminuir recuo", shortcut: "Shift+Tab", action: () => this.outdent() },
                { icon: "mdi-format-indent-increase", label: "Aumentar recuo", shortcut: "Tab", action: () => this.indent() },
                { divider: true },
                { icon: "mdi-format-quote-close", label: "Citação", active: this.editor.isActive("blockquote"), action: () => this.run((c) => c.toggleBlockquote()) },
                { icon: "mdi-link-variant", label: "Link", active: this.editor.isActive("link"), action: () => this.$emit("link") },
                { divider: true },
                { icon: "mdi-format-clear", label: "Limpar formatação", action: () => this.clearFormatting() },
            ];
        },
        currentAlign() {
            void this.tick;
            return this.aligns.find((align) => this.editor.isActive({ textAlign: align.value })) || this.aligns[3];
        },
        extraMarkActive() {
            void this.tick;
            return this.extraMarks.some((mark) => this.editor.isActive(mark.name));
        },
        currentFontLabel() {
            void this.tick;
            const family = this.editor.getAttributes("textStyle").fontFamily;
            const font = FONT_FAMILIES.find((item) => item.value === (family || null));
            return font ? font.text.replace(" (padrão)", "") : "Segoe UI";
        },
        currentFontSize() {
            void this.tick;
            return this.editor.getAttributes("textStyle").fontSize || DEFAULT_FONT_SIZE;
        },
        currentColor() {
            void this.tick;
            return this.editor.getAttributes("textStyle").color || null;
        },
        currentHighlight() {
            void this.tick;
            return this.editor.getAttributes("highlight").color || null;
        },
    },
    mounted() {
        this.width = this.$el.clientWidth;
        if (typeof ResizeObserver === "function") {
            this.resizeObserver = new ResizeObserver(([entry]) => { this.width = entry.contentRect.width; });
            this.resizeObserver.observe(this.$el);
        }
    },
    beforeDestroy() {
        if (this.resizeObserver) this.resizeObserver.disconnect();
    },
    methods: {
        run(action) {
            action(this.editor.chain().focus()).run();
        },
        can(command) {
            void this.tick;
            return this.editor.can()[command]();
        },
        isActive(name, attrs) {
            void this.tick;
            return this.editor.isActive(name, attrs);
        },
        isAlign(value) {
            void this.tick;
            if (this.editor.isActive("image")) return this.editor.isActive("image", { align: value });
            return this.editor.isActive({ textAlign: value });
        },
        setAlign(value) {
            // Com uma imagem selecionada, o alinhamento e da imagem (sem justificar).
            if (this.editor.isActive("image")) {
                if (value !== "justify") this.run((c) => c.setImageAlign(value));
                return;
            }
            this.run((c) => c.setTextAlign(value));
        },
        clearFormatting() {
            const normal = findStyle("texto-normal") || activeStyles("paragraph")[0];
            this.run((c) => c.unsetAllMarks().applyParagraphStyle(normal));
        },
        stylePreview(style) {
            const size = parseFloat(style.fontSize || "11");
            return { ...previewCss(style), fontSize: `${Math.min(18, Math.max(12, size * 1.2))}px`, textAlign: "left" };
        },
        setFont(value) {
            if (value) this.run((c) => c.setFontFamily(value));
            else this.run((c) => c.unsetFontFamily());
        },
        stepFontSize(direction) {
            const index = FONT_SIZES.indexOf(this.currentFontSize);
            const next = FONT_SIZES[Math.min(FONT_SIZES.length - 1, Math.max(0, (index === -1 ? FONT_SIZES.indexOf(DEFAULT_FONT_SIZE) : index) + direction))];
            this.run((c) => c.setFontSize(next));
        },
        indent() {
            if (this.editor.isActive("listItem")) this.run((c) => c.sinkListItem("listItem"));
            else this.run((c) => c.indent());
        },
        outdent() {
            if (this.editor.isActive("listItem")) this.run((c) => c.liftListItem("listItem"));
            else this.run((c) => c.outdent());
        },
        setCellColor(color) {
            this.run((c) => c.setCellAttribute("backgroundColor", color ? color.bg : null).setCellAttribute("textColor", color ? color.fg : null));
        },
        formatMargins(preset) {
            const value = (n) => String(n).replace(".", ",");
            return `Sup. ${value(preset.top)} · Inf. ${value(preset.bottom)} · Esq. ${value(preset.left)} · Dir. ${value(preset.right)} cm`;
        },
    },
};
</script>
